import {createAsyncThunk} from "@reduxjs/toolkit";
import type {User} from "@supabase/supabase-js";
import type {Dealership, DealerUser} from "@/types";
import {supabase} from "@/utils/supabase.ts";
import {getAuthCookies, keysToCamelCase, keysToSnakeCase, normalizeError, setAuthCookies} from "@/utils";

export interface AuthSuccessPayload {
    user?: Dealership;
    dealerUser?: DealerUser;
    redirectTo: string;
    onboardingRequired: boolean;
}

type LoginCredentials = {
    email: string;
    password: string;
    remember?: boolean;
};

function isOnboardingRequired(dealer?: Dealership) {
    if (!dealer) {
        return true;
    }

    return !dealer.name?.trim()
        || !dealer.email?.trim()
        || !dealer.phone?.trim()
        || !dealer.locations?.length
        || !dealer.images?.length;
}

export async function resolveAuthenticatedDealerProfile(authUser: User) {
    const dealerResponse = await supabase
        .from("dealer_users")
        .select("*, dealer:dealerships(*)")
        .eq("id", authUser.id)
        .maybeSingle();

    if (dealerResponse.error) {
        throw new Error(dealerResponse.error.message);
    }
    if (!dealerResponse.data) {
        const legacyDealerResponse = await supabase
            .from("dealerships")
            .select("*")
            .eq("id", authUser.id)
            .maybeSingle();

        if (legacyDealerResponse.error) {
            throw new Error(legacyDealerResponse.error.message);
        }

        if (!legacyDealerResponse.data) {
            return {
                dealerUser: undefined,
                onboardingRequired: true,
                redirectTo: "/wizard",
            }
        }

        const createdDealerUserResponse = await supabase
            .from("dealer_users")
            .upsert(keysToSnakeCase({
                id: authUser.id,
                dealerId: legacyDealerResponse.data.id,
                firstName: authUser.user_metadata?.firstName ?? "",
                lastName: authUser.user_metadata?.lastName ?? "",
                email: authUser.email ?? legacyDealerResponse.data.email ?? "",
                role: "admin",
                profile: undefined
            }), {onConflict: "id"})
            .select("*")
            .single();

        if (createdDealerUserResponse.error) {
            throw new Error(createdDealerUserResponse.error.message);
        }

        const dealerRecord = keysToCamelCase<Dealership>(legacyDealerResponse.data);
        const dealerUser = keysToCamelCase<DealerUser>(createdDealerUserResponse.data);
        const onboardingRequired = isOnboardingRequired(dealerRecord);

        return {
            user: dealerRecord,
            dealerUser,
            onboardingRequired,
            redirectTo: onboardingRequired ? "/wizard" : "/"
        } satisfies AuthSuccessPayload;
    }

    const dealerUser = keysToCamelCase<DealerUser>(dealerResponse.data);
    const dealerRecord = dealerResponse.data.dealer
        ? keysToCamelCase<Dealership>(dealerResponse.data.dealer)
        : undefined;
    const onboardingRequired = isOnboardingRequired(dealerRecord);

    return {
        user: dealerRecord,
        dealerUser,
        onboardingRequired,
        redirectTo: onboardingRequired ? "/wizard" : "/"
    } satisfies AuthSuccessPayload;
}

export const loginAsync = createAsyncThunk<AuthSuccessPayload, LoginCredentials, { rejectValue: string }>(
    "authentication/login",
    async (credentials, {rejectWithValue}) => {
        try {
            const {data, error} = await supabase.auth.signInWithPassword({
                email: credentials.email,
                password: credentials.password
            });

            if (error) {
                return rejectWithValue(error.message);
            }

            const authUser = data.user;
            if (!authUser) {
                return rejectWithValue("Unable to find authenticated user");
            }

            if (data.session) {
                setAuthCookies(data.session);
            }

            return await resolveAuthenticatedDealerProfile(authUser);
        } catch (error: unknown) {
            return rejectWithValue(normalizeError(error, "Failed to login"));
        }
    }
);

export const restoreSessionAsync = createAsyncThunk<AuthSuccessPayload | undefined, void, { rejectValue: string }>(
    "authentication/restoreSession",
    async (_, {rejectWithValue}) => {
        try {
            const tokens = getAuthCookies();

            if (!tokens) {
                return undefined;
            }

            const {data, error} = await supabase.auth.setSession({
                access_token: tokens.accessToken,
                refresh_token: tokens.refreshToken
            });

            if (error) {
                return rejectWithValue(error.message);
            }

            if (!data.session?.user) {
                return undefined;
            }

            setAuthCookies(data.session);

            return await resolveAuthenticatedDealerProfile(
                data.session.user
            );
        } catch (error) {
            return rejectWithValue(normalizeError(error, "Failed to restore session"));
        }
    }
);
